import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileDown, FilePlus2, Pencil, RefreshCw, Undo2, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorText, openFile, unwrap, uploadForm } from '../../api/client';
import type { Disposal } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { AssetLink } from '../../components/assets/AssetLink';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  DataList,
  Dialog,
  ErrorBox,
  Field,
  Input,
  Loading,
  nullIfEmpty,
  PageHeader,
  Select,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
  useToast,
} from '../../components/ui';
import { CHILD_ACTION_LABELS, DOCUMENT_CATEGORY_LABELS, formatBytes, formatDate, formatDateTime, formatMoney, METHOD_LABELS } from '../../lib/format';

export function DisposalDetailPage() {
  const { ref = '' } = useParams();
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reverting, setReverting] = useState(false);
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['disposal', ref],
    queryFn: () => unwrap(api.GET('/api/disposals/{ref}', { params: { path: { ref } } })),
  });

  const regenerate = useMutation({
    mutationFn: () => unwrap(api.POST('/api/disposals/{ref}/protocol', { params: { path: { ref } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['disposal', ref] });
      void qc.invalidateQueries({ queryKey: ['asset'] });
      toast('Protokoll erzeugt und am Gerät gespeichert');
    },
    onError: (e) => toast(errorText(e), 'error'),
  });

  const revert = useMutation({
    mutationFn: (reason: string) =>
      unwrap(api.DELETE('/api/disposals/{ref}', { params: { path: { ref } }, body: { reason } })),
    onSuccess: (r) => {
      void qc.invalidateQueries();
      toast(`Verschrottung ${r.disposal_number} zurückgenommen – ${r.restored_assets.join(', ')} wiederhergestellt`);
      navigate('/disposals');
    },
  });

  if (q.isPending) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data;
  const primaries = d.items.filter((i) => i.role === 'primary');
  const children = d.items.filter((i) => i.role === 'child');
  const snap = (s: Record<string, unknown>, k: string) => s[k] as string | number | null | undefined;

  const pdf = (assetNumber: string) =>
    openFile(`/api/disposals/${encodeURIComponent(d.disposal_number)}/protocol?format=pdf&asset=${encodeURIComponent(assetNumber)}`).catch((e: unknown) =>
      toast(errorText(e), 'error'),
    );

  return (
    <>
      <PageHeader
        back={
          <Link to="/disposals" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800">
            <ArrowLeft className="size-4" /> Verschrottungen
          </Link>
        }
        title={
          <span>
            Verschrottung <span className="font-mono">{d.disposal_number}</span>
          </span>
        }
        subtitle={`${formatDate(d.disposal_date)} · ${METHOD_LABELS[d.method] ?? d.method}`}
        actions={
          <>
            {primaries.map((p) => (
              <Button key={p.id} icon={<FileDown className="size-4" />} onClick={() => pdf(p.asset.asset_number)}>
                Protokoll {primaries.length > 1 ? p.asset.asset_number : ''}
              </Button>
            ))}
            {can('asset:dispose') && (
              <>
                <Button icon={<RefreshCw className="size-4" />} loading={regenerate.isPending} onClick={() => regenerate.mutate()}>
                  Protokoll speichern
                </Button>
                <Button icon={<Pencil className="size-4" />} onClick={() => setEditing(true)}>
                  Nachtragen
                </Button>
              </>
            )}
            {can('asset:delete') && (
              <Button variant="danger" icon={<Undo2 className="size-4" />} onClick={() => setReverting(true)}>
                Zurücknehmen
              </Button>
            )}
          </>
        }
      />
      <ConfirmDialog
        open={reverting}
        onClose={() => {
          setReverting(false);
          revert.reset();
        }}
        title={`Verschrottung ${d.disposal_number} zurücknehmen?`}
        message={
          <>
            Für versehentliche oder Test-Verschrottungen: {d.items.map((i) => i.asset.asset_number).join(', ')} werden wiederhergestellt – mit Status,
            Mitarbeiter, Standort, Einbauten und Teilen wie vorher. Der Vorgang verschwindet aus allen Listen und Berichten, das Protokoll wird
            gelöscht. In der Historie der Geräte bleibt beides sichtbar.
          </>
        }
        confirmLabel="Zurücknehmen"
        danger
        requireReason
        reasonLabel="Grund (z. B. „Test“)"
        loading={revert.isPending}
        error={revert.error}
        onConfirm={(reason) => revert.mutate(reason)}
      />

      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Angaben" />
          <CardBody>
            <DataList
              items={[
                ['Grund', <span className="whitespace-pre-line">{d.reason}</span>],
                ['Zustand', d.condition],
                ['Art der Entsorgung', METHOD_LABELS[d.method] ?? d.method],
                ['Durchgeführt von', d.performed_by_name],
                ['Entsorgungsunternehmen', d.disposal_company],
                ['Nachweis-Nr.', d.certificate_number],
                ['Erfasst von', d.responsible?.display_name],
                ['Erfasst am', formatDateTime(d.created_at)],
                !!d.notes && ['Bemerkungen', <span className="whitespace-pre-line">{d.notes}</span>],
              ]}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Dokumente"
            actions={
              can('attachment:upload') && (
                <Button size="sm" icon={<Upload className="size-4" />} onClick={() => setUploading(true)}>
                  Nachweis
                </Button>
              )
            }
          />
          {d.documents.length === 0 ? (
            <CardBody className="text-sm text-slate-500">Noch keine Dokumente. „Protokoll speichern“ legt das PDF am Gerät ab.</CardBody>
          ) : (
            <ul className="divide-y divide-slate-100">
              {d.documents.map((doc) => (
                <li key={doc.id} className="px-4 py-2.5 text-sm">
                  <button type="button" className="text-left font-medium text-brand-700 hover:underline" onClick={() => openFile(doc.content_url).catch((e: unknown) => toast(errorText(e), 'error'))}>
                    {doc.title || doc.original_filename}
                  </button>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Badge>{DOCUMENT_CATEGORY_LABELS[doc.category] ?? doc.category}</Badge>
                    {doc.asset_number} · {formatBytes(doc.size_bytes)} · {formatDateTime(doc.uploaded_at)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader title="Verschrottete Geräte" />
          <Table>
            <thead>
              <tr>
                <Th>Nr.</Th>
                <Th>Bezeichnung</Th>
                <Th>Seriennummer</Th>
                <Th>Zuletzt bei</Th>
                <Th>Kaufdatum</Th>
                <Th className="text-right">Kosten netto</Th>
              </tr>
            </thead>
            <tbody>
              {d.items
                .filter((i) => i.action === 'dispose')
                .map((i) => (
                  <Tr key={i.id}>
                    <Td className="whitespace-nowrap">
                      <AssetLink asset={i.asset} />
                      {i.role === 'child' && <div className="text-xs text-slate-500">in {i.original_parent?.asset_number}</div>}
                    </Td>
                    <Td>{i.asset.name}</Td>
                    <Td className="font-mono text-xs">{snap(i.snapshot, 'serial_number') ?? '–'}</Td>
                    <Td>{[i.last_employee?.short_code, i.last_location?.name].filter(Boolean).join(' · ') || '–'}</Td>
                    <Td>{formatDate(snap(i.snapshot, 'purchase_date') as string | null)}</Td>
                    <Td className="text-right whitespace-nowrap">{formatMoney(snap(i.snapshot, 'cost_net_cents') as number | null)}</Td>
                  </Tr>
                ))}
            </tbody>
          </Table>
        </Card>

        {children.some((c) => c.action !== 'dispose') && (
          <Card className="lg:col-span-3">
            <CardHeader title="Weiterverwendete Komponenten" />
            <Table>
              <thead>
                <tr>
                  <Th>Nr.</Th>
                  <Th>Bezeichnung</Th>
                  <Th>Aus</Th>
                  <Th>Entscheidung</Th>
                  <Th>Ziel</Th>
                </tr>
              </thead>
              <tbody>
                {children
                  .filter((c) => c.action !== 'dispose')
                  .map((c) => (
                    <Tr key={c.id}>
                      <Td>
                        <AssetLink asset={c.asset} />
                      </Td>
                      <Td>{c.asset.name}</Td>
                      <Td>{c.original_parent?.asset_number ?? '–'}</Td>
                      <Td>{CHILD_ACTION_LABELS[c.action]}</Td>
                      <Td>{c.target_asset ? <AssetLink asset={c.target_asset} /> : (c.target_location?.name ?? '–')}</Td>
                    </Tr>
                  ))}
              </tbody>
            </Table>
          </Card>
        )}
      </div>

      <EditDisposalDialog disposal={d} open={editing} onClose={() => setEditing(false)} />
      <UploadDisposalDocDialog disposal={d} open={uploading} onClose={() => setUploading(false)} />
    </>
  );
}

function EditDisposalDialog({ disposal, open, onClose }: { disposal: Disposal; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState({ disposal_company: '', certificate_number: '', notes: '' });
  useEffect(() => {
    if (open) setF({ disposal_company: disposal.disposal_company ?? '', certificate_number: disposal.certificate_number ?? '', notes: disposal.notes ?? '' });
  }, [open, disposal]);
  const m = useMutation({
    mutationFn: () =>
      unwrap(
        api.PATCH('/api/disposals/{ref}', {
          params: { path: { ref: disposal.id } },
          body: { disposal_company: nullIfEmpty(f.disposal_company), certificate_number: nullIfEmpty(f.certificate_number), notes: nullIfEmpty(f.notes) },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['disposal'] });
      void qc.invalidateQueries({ queryKey: ['disposals'] });
      toast('Gespeichert');
      onClose();
    },
  });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Angaben nachtragen"
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <p className="text-sm text-slate-600">Grund, Datum und Geräte sind fest. Nachtragen lassen sich Entsorger, Nachweis-Nr. und Bemerkungen.</p>
      <Field label="Entsorgungsunternehmen">{(p) => <Input {...p} value={f.disposal_company} onChange={(e) => setF({ ...f, disposal_company: e.target.value })} />}</Field>
      <Field label="Nachweis-Nr.">{(p) => <Input {...p} value={f.certificate_number} onChange={(e) => setF({ ...f, certificate_number: e.target.value })} />}</Field>
      <Field label="Bemerkungen">{(p) => <Textarea {...p} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />}</Field>
      <ErrorBox error={m.error} />
    </Dialog>
  );
}

function UploadDisposalDocDialog({ disposal, open, onClose }: { disposal: Disposal; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState('disposal_certificate');
  const [title, setTitle] = useState('');
  useEffect(() => {
    if (open) {
      setFile(null);
      setCategory('disposal_certificate');
      setTitle('');
    }
  }, [open]);
  const m = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('file', file!);
      form.append('category', category);
      if (title.trim()) form.append('title', title.trim());
      return uploadForm(`/api/disposals/${encodeURIComponent(disposal.id)}/documents`, form);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['disposal'] });
      void qc.invalidateQueries({ queryKey: ['asset'] });
      toast('Dokument gespeichert');
      onClose();
    },
  });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Dokument zum Vorgang"
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending} disabled={!file} icon={<FilePlus2 className="size-4" />}>
            Hochladen
          </Button>
        </>
      }
    >
      <Field label="Datei" required>
        {(p) => <input {...p} type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-brand-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-brand-700" />}
      </Field>
      <Field label="Kategorie">
        {(p) => (
          <Select {...p} value={category} onChange={(e) => setCategory(e.target.value)}>
            {['disposal_certificate', 'disposal_protocol', 'invoice', 'other'].map((k) => (
              <option key={k} value={k}>
                {DOCUMENT_CATEGORY_LABELS[k]}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Titel (optional)">{(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} />}</Field>
      <ErrorBox error={m.error} />
    </Dialog>
  );
}
